import { useEffect, useMemo, useState } from "react";
import type { FixtureFile } from "../lib/fixtures";
import { decodeSnapshot, formatByteDelta, formatBytes, mediaTypeFor, type MediaType } from "../lib/media-preview";
import { ExpandableMedia } from "./ExpandableMedia";

interface PreviewSide {
  label: "Before" | "After";
  bytes: Uint8Array;
}

function useObjectUrl(bytes: Uint8Array | undefined, mime: string | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!bytes || !mime) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [bytes, mime]);
  return url;
}

function MediaPane({ side, type, path, onUnrenderable }: {
  side: PreviewSide;
  type: MediaType;
  path: string;
  onUnrenderable: () => void;
}) {
  const url = useObjectUrl(side.bytes, type.mime);
  const [dimensions, setDimensions] = useState<string>();
  const label = `${side.label}: ${path}`;
  return (
    <figure className={`binary-preview-pane binary-preview-${side.label.toLowerCase()}`}>
      <figcaption>
        <strong>{side.label}</strong>
        <span>{[dimensions, formatBytes(side.bytes.byteLength)].filter(Boolean).join(" · ")}</span>
      </figcaption>
      {url && type.kind === "image" && (
        <ExpandableMedia className="binary-preview-stage" description={formatBytes(side.bytes.byteLength)} label={label}>
          <img
            alt={label}
            onError={onUnrenderable}
            onLoad={(event) => setDimensions(`${event.currentTarget.naturalWidth}×${event.currentTarget.naturalHeight}`)}
            src={url}
          />
        </ExpandableMedia>
      )}
      {url && type.kind === "audio" && (
        <audio className="binary-preview-audio" controls onError={onUnrenderable} preload="metadata" src={url} />
      )}
      {url && type.kind === "video" && (
        <video
          className="binary-preview-video"
          controls
          onError={onUnrenderable}
          onLoadedMetadata={(event) => setDimensions(`${event.currentTarget.videoWidth}×${event.currentTarget.videoHeight}`)}
          preload="metadata"
          src={url}
        />
      )}
      {url && type.kind === "pdf" && (
        <iframe className="binary-preview-pdf" src={url} title={label} />
      )}
    </figure>
  );
}

export function BinaryFilePreview({ file, path }: { file: FixtureFile; path: string }) {
  const sides = useMemo(() => {
    const base = decodeSnapshot(file.base, file.encoding);
    const head = decodeSnapshot(file.head, file.encoding);
    return {
      base: base.byteLength > 0 ? base : undefined,
      head: head.byteLength > 0 ? head : undefined,
    };
  }, [file.base, file.head, file.encoding]);
  const type = useMemo(() => mediaTypeFor(path, [sides.head, sides.base]), [path, sides]);
  const [unrenderable, setUnrenderable] = useState(false);
  useEffect(() => setUnrenderable(false), [sides]);

  const panes: PreviewSide[] = [
    ...(sides.base ? [{ label: "Before" as const, bytes: sides.base }] : []),
    ...(sides.head ? [{ label: "After" as const, bytes: sides.head }] : []),
  ];
  const summary = sides.base && sides.head
    ? `${formatBytes(sides.base.byteLength)} → ${formatBytes(sides.head.byteLength)} · ${formatByteDelta(sides.base.byteLength, sides.head.byteLength)}`
    : sides.head
      ? `Added · ${formatBytes(sides.head.byteLength)}`
      : sides.base
        ? `Removed · ${formatBytes(sides.base.byteLength)}`
        : "Empty file";

  if (!type || unrenderable || panes.length === 0) {
    return (
      <div className="missing-code">
        <strong>Binary change</strong>
        <p>{unrenderable ? "Your browser could not render this file. " : "No preview for this file type. "}{summary}</p>
      </div>
    );
  }

  return (
    <div className={`binary-preview binary-preview-${type.kind}${panes.length === 2 ? " is-comparison" : ""}`}>
      <div className="binary-preview-panes">
        {panes.map((side) => (
          <MediaPane key={side.label} onUnrenderable={() => setUnrenderable(true)} path={path} side={side} type={type} />
        ))}
      </div>
      <p className="binary-preview-summary">{summary}</p>
    </div>
  );
}
